import type { AxiosRequestConfig } from 'axios';
import axiosInstance from "../api/axiosInstance";
import {
  User,
  UserFilterParams,
  PaginatedUsersResponse,
  UserCreatePayload,
  UserUpdatePayload,
  UserPasswordUpdate,
  PaginatedUserPasswordHistoryResponse,
  UserBulkActionRequest,
  UserActivitySummary,
  UserStreak,
  UserActivityHistory,
  StreakSettings,
  UpdateStreakSettingsPayload,
  CreateMilestonePayload,
  StreakMilestone,
  PaginatedUserLearningResponse
} from "../types/users.types";
import { buildQueryString } from "../utils/query";
import { ExportFormat } from "../constants/export-dropdown.constants";
import { UserCertificate } from "@/components/admin/Courses/CourseBuilder/types";
import { USER_ENDPOINTS } from "@/api/endpoints/user.endpoints";
import { ManagementApiService, FormApiService } from "@/types/management.types";

type BulkActionResponse = {
  updatedCount?: number;
  createdCount?: number;
  [key: string]: unknown;
};

type UploadImageResponse = { url: string; id?: number; [key: string]: unknown };

export const UserManagementApiService: ManagementApiService<
  User,
  UserFilterParams
> = {
  getAll: async (
    offset = 0,
    limit = 20,
    filters?: UserFilterParams
  ): Promise<PaginatedUsersResponse> => {
    const query = buildQueryString({
      ...filters,
      offset,
      limit,
      with_relations: 'job_assignments.department,roles'
    });
    return (
      await axiosInstance.get<PaginatedUsersResponse>(
        `${USER_ENDPOINTS.USER}${query}`
      )
    ).data;
  },
  deleteItem: async (id: number): Promise<void> => {
    await axiosInstance.delete(USER_ENDPOINTS.USER_DETAIL(id));
  },
  exportItems: async (
    format: ExportFormat,
    filters?: Omit<UserFilterParams, "offset" | "limit">
  ): Promise<Blob> => {
    const queryParams = { ...filters, format: format.toLowerCase() };
    const query = buildQueryString(queryParams);
    const response = await axiosInstance.get<Blob>(
      `${USER_ENDPOINTS.USERS_EXPORT}${query}`,
      { responseType: "blob" }
    );
    return response.data;
  },
};

export const UserFormApiService: FormApiService<
  User,
  UserCreatePayload,
  UserUpdatePayload
> = {
  getItem: async (id: number | string): Promise<User> =>
    (await axiosInstance.get<User>(USER_ENDPOINTS.USER_DETAIL(id))).data,
  createItem: async (data: UserCreatePayload): Promise<User> =>
    (await axiosInstance.post<User>(USER_ENDPOINTS.USER, data)).data,
  updateItem: async (id: number, data: UserUpdatePayload): Promise<User> =>
    (await axiosInstance.put<User>(USER_ENDPOINTS.USER_DETAIL(id), data)).data,
};

export const UserExtraApiService = {
  setUserPassword: async (
    id: number,
    data: UserPasswordUpdate
  ): Promise<void> => {
    await axiosInstance.put(USER_ENDPOINTS.USER_PASSWORD(id), data);
  },
  /** The signed-in user's own password; also clears a pending "choose a new password" request. */
  changeMyPassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await axiosInstance.put(USER_ENDPOINTS.MY_PASSWORD, {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
  getUserPasswordHistory: async (
    id: number,
    offset = 0,
    limit = 20
  ): Promise<PaginatedUserPasswordHistoryResponse> => {
    const query = buildQueryString({ offset, limit });
    return (
      await axiosInstance.get<PaginatedUserPasswordHistoryResponse>(
        `${USER_ENDPOINTS.PASSWORD_HISTORY(id)}${query}`
      )
    ).data;
  },
  performBulkAction: async (
    data: UserBulkActionRequest
  ): Promise<BulkActionResponse> =>
    (
      await axiosInstance.post<BulkActionResponse>(
        USER_ENDPOINTS.USERS_BULK_ACTIONS,
        data
      )
    ).data,
  getUserProfileFieldsData: async (userId: number): Promise<Record<string, any>> => {
    try {
      const response = await axiosInstance.get(USER_ENDPOINTS.USER_PROFILE_DATA_GET(userId));
      const fieldsData: Record<string, any> = {};
      const items = response.data?.items || (Array.isArray(response.data) ? response.data : []);
      if (Array.isArray(items)) {
        for (const field of items) {
          if (field.shortname) {
            fieldsData[field.shortname] = field.user_value;
          }
        }
      }
      return fieldsData;
    } catch (error: any) {
      if (error.response?.status === 403) {
        return {};
      }
      throw error;
    }
  },
  updateUserProfileFieldsData: async (userId: number, data: Record<string, any>): Promise<any> => {
    return (await axiosInstance.put(USER_ENDPOINTS.USER_PROFILE_DATA_UPDATE(userId), { data })).data;
  }
};

export const UserImageApiService = {
  uploadProfileImage: async (
    id: number,
    file: File
  ): Promise<UploadImageResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    return (
      await axiosInstance.post<UploadImageResponse>(
        USER_ENDPOINTS.PROFILE_IMAGE(id),
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
    ).data;
  },
  getProfileImage: async (id: number): Promise<string> => {
    try {
      const res = await axiosInstance.get<Blob>(
        USER_ENDPOINTS.PROFILE_IMAGE(id),
        {
          responseType: "blob",
        }
      );
      if (res.data.size === 0) return "";
      return URL.createObjectURL(res.data);
    } catch {
      return "";
    }
  },
  deleteProfileImage: async (id: number): Promise<void> => {
    await axiosInstance.delete(USER_ENDPOINTS.PROFILE_IMAGE(id));
  },
};

// ── Self-service profile editing (lock-aware) ────────────────────────────────
// Backed by /v1/users/me/* — locked fields (per the tenant's per-auth-method
// policy, see /admin/server/field-locks) are read-only for the user themselves.

export interface ProfileFormCustomField {
  shortname: string;
  label: string;
  datatype: string;
  required: boolean;
  config?: Record<string, any> | string | null;
  value: any;
  locked: boolean;
}

export interface MyProfileFormCore {
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  email?: string | null;
  mobile?: string | null;
  summary?: string | null;
  lang?: string | null;
  country_code?: string | null;
}

export interface MyProfileForm {
  loginmethod: string;
  core: MyProfileFormCore;
  locked_core: string[];
  custom_fields: ProfileFormCustomField[];
}

export interface MyProfileUpdateResult {
  updated: string[];
  locked_skipped: string[];
}

/**
 * True when a profile-field value counts as EMPTY for required-field checks
 * (used by the Edit Profile modal validation and the post-login completion
 * gate). Handles both raw API values and normalized form values:
 * null / undefined / '' / whitespace, empty arrays, and empty JSON-array
 * strings ("[]") are empty. Booleans are never empty — a checkbox always
 * carries a value.
 */
export const isEmptyProfileFieldValue = (datatype: string, value: any): boolean => {
  if (datatype === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  if (value === null || value === undefined) return true;
  const s = String(value).trim();
  if (s === '') return true;
  if (datatype === 'dropdown_multi' && s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) return parsed.length === 0;
    } catch { /* not JSON — treat as a plain non-empty string */ }
  }
  return false;
};

export const UserProfileApiService = {
  /** Everything the self-service Edit Profile form needs (values + locks). */
  getMyProfileForm: async (): Promise<MyProfileForm> =>
    // Raw values: names keep every language ({en}…{en}{ar}…{ar}) for the per-language fields.
    (await axiosInstance.get<MyProfileForm>('/v1/users/me/profile-form', { skipMultilangStrip: true } as AxiosRequestConfig)).data,
  /** Update own core fields — locked fields are silently skipped server-side. */
  updateMyProfile: async (data: MyProfileFormCore): Promise<MyProfileUpdateResult> =>
    (await axiosInstance.put<MyProfileUpdateResult>('/v1/users/me/profile', data)).data,
  /** Update own custom-field values — body {data: {shortname: value}}. */
  updateMyProfileData: async (data: Record<string, any>): Promise<MyProfileUpdateResult> =>
    (await axiosInstance.put<MyProfileUpdateResult>('/v1/users/me/profile-data', { data })).data,
};

export const UserCertificateApiService = {
  getAllForCurrentUser: async (): Promise<UserCertificate[]> => {
    const response = await axiosInstance.get<UserCertificate[]>(
      `/v1/users/me/certificates`
    );
    return response.data;
  },
};

export const UserStatsApiService = {
  getActivitySummary: async (userId: number | string): Promise<UserActivitySummary> => {
    return (await axiosInstance.get<UserActivitySummary>(USER_ENDPOINTS.ACTIVITY_SUMMARY(userId))).data;
  },
  getActivityHistory: async (userId: number | string, from: string, to: string): Promise<UserActivityHistory> => {
    const query = buildQueryString({ from, to });
    return (await axiosInstance.get<UserActivityHistory>(`${USER_ENDPOINTS.ACTIVITY_HISTORY(userId)}${query}`)).data;
  },
  getStreak: async (userId: number | string): Promise<UserStreak> => {
    return (await axiosInstance.get<UserStreak>(USER_ENDPOINTS.STREAK(userId))).data;
  },
  getStreakSettings: async (): Promise<StreakSettings> => {
    return (await axiosInstance.get<StreakSettings>(USER_ENDPOINTS.STREAK_SETTINGS)).data;
  },
  updateStreakSettings: async (data: UpdateStreakSettingsPayload): Promise<StreakSettings> => {
    return (await axiosInstance.put<StreakSettings>(USER_ENDPOINTS.STREAK_SETTINGS, data)).data;
  },
  addStreakMilestone: async (data: CreateMilestonePayload): Promise<StreakMilestone> => {
    return (await axiosInstance.post<StreakMilestone>(USER_ENDPOINTS.STREAK_MILESTONES, data)).data;
  },
  updateStreakMilestone: async (id: number | string, data: CreateMilestonePayload): Promise<StreakMilestone> => {
    return (await axiosInstance.put<StreakMilestone>(USER_ENDPOINTS.STREAK_MILESTONE_DETAIL(id), data)).data;
  },
  deleteStreakMilestone: async (milestoneId: number | string): Promise<void> => {
    await axiosInstance.delete(USER_ENDPOINTS.STREAK_MILESTONE_DETAIL(milestoneId));
  },
};

/** A user as stored — names and description in every language ({en}…{en}{ar}…{ar}), not resolved for the page:
 *  for the forms that edit them (resolving would drop the other languages on save). */
export const getUserRaw = async (id: number | string): Promise<User> =>
  (await axiosInstance.get<User>(USER_ENDPOINTS.USER_DETAIL(id), { skipMultilangStrip: true } as AxiosRequestConfig)).data;

export const UserLearningApiService = {
  getMyLearning: async (offset = 0, limit = 20): Promise<PaginatedUserLearningResponse> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<PaginatedUserLearningResponse>(`${USER_ENDPOINTS.MY_LEARNING}${query}`)).data;
  },
  getUserLearning: async (userId: number | string, offset = 0, limit = 20): Promise<PaginatedUserLearningResponse> => {
    const query = buildQueryString({ offset, limit });
    return (await axiosInstance.get<PaginatedUserLearningResponse>(`${USER_ENDPOINTS.USER_LEARNING(userId)}${query}`)).data;
  },
  /** Map of { courseId: progressPercent } for the user's in-progress courses. */
  getMyCourseProgress: async (): Promise<Record<number, number>> =>
    (await axiosInstance.get<Record<number, number>>(USER_ENDPOINTS.MY_COURSE_PROGRESS)).data,
};

export const UserApi = {
  Management: UserManagementApiService,
  Form: UserFormApiService,
  Extra: UserExtraApiService,
  Profile: UserProfileApiService,
  Image: UserImageApiService,
  Certificates: UserCertificateApiService,
  Stats: UserStatsApiService,
  Learning: UserLearningApiService,
};